import React, {ReactNode} from "react";
import ContentMode from "../ContentMode";
import Administration from "../session/Administration";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import ProgressBar from "../../shared/components/ProgressBar";
import {AbstractPanel, AbstractPanelProperties, AbstractPanelState} from "./AbstractPanel";
import {
	AdminWebserviceStatus,
	AggregationServerState,
	DataSourceServerState,
	JobServerState,
	PoolServerState,
	ServerStatus,
	Statistic as AdminStatistic,
	Webservice
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../shared/translation/Translate";
import {TabPanel, TabView} from "primereact/tabview";
import MenuButton from "../../shared/menu/MenuButton";
import {Dropdown} from "primereact/dropdown";
import Exception from "../../shared/exception/Exception";
import {Statistic} from "./statistic/Statistic";
import {CanvasLayer, CustomCanvasLayerProps, Serie} from '@nivo/line';
import TimeUnit from "../../shared/units/TimeUnit";
import LineChart from "./statistic/LineChart";
import {MultiSelect} from "primereact/multiselect";
import DateHelper from "../../shared/helper/DateHelper";
import NumberHelper from "../../shared/helper/NumberHelper";
import PieChart from "./statistic/PieChart";
import {InputNumber} from "primereact/inputnumber";
import Helper from "../../shared/components/Helper";
import Info, {InfoType} from "../../shared/components/Info";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import Timer from "../../shared/class/Timer";

enum WebserviceStatus {
	OK = "ok",
	DISABLED = "disabled",
	ERROR = "error",
	NOT_LICENSED = "licensed"
}

// mode = minutes
enum ChartMode {
	HOUR = 60,
	SIX_HOURS = 360,
	TWELVE_HOURS = 720,
	DAY = 1440,
	THREE_DAYS = 4320,
	WEEK = 10080,
	MONTH = 43200
}

enum StatisticPanel {
	OVERVIEW,
	JOBS,
	USAGE,
	ERROR,
	TIME,
	FORMAT
}

interface WebserviceItem {
	name: string;
	service: AdminWebserviceStatus;
}

export interface StatisticsPanelProperties extends AbstractPanelProperties {
}

export interface StatisticsPanelState extends AbstractPanelState {
	update: boolean;
	timer: number;
	panel: StatisticPanel;
	usageAggregation: AggregationServerState;
	usageMode: ChartMode;
	usageHidden: Array<Webservice>;
	usageQueue: Array<Serie>;
	usagePool: Array<Serie>;
	jobsAggregation: AggregationServerState;
	jobsMode: ChartMode;
	jobsHidden: Array<Webservice>;
	jobs: Array<Serie>;
	averageAggregation: AggregationServerState;
	averageMode: ChartMode;
	averageHidden: Array<Webservice>;
	average: Array<Serie>;
	formatAggregation: AggregationServerState;
	formatMode: ChartMode;
	formatHidden: Array<string>;
	formatJobs: Array<any>;
	formatAverage: Array<any>;
	formatType: Webservice;
	formatLabelHide: number;
	errorAggregation: AggregationServerState;
	errorMode: ChartMode;
	errorHidden: Array<Webservice>;
	error: Array<any>;
}

export class StatisticsPanelDefinition extends AbstractPanel<StatisticsPanelProperties, StatisticsPanelState> {
	private updateTimer: Timer;

	constructor(props: Readonly<StatisticsPanelProperties>) {
		super(props);

		this.state = {
			...super.state,
			panel: StatisticPanel.OVERVIEW,
			usageAggregation: AggregationServerState.Minute,
			usageMode: ChartMode.HOUR,
			usageHidden: [],
			usageQueue: [],
			usagePool: [],
			jobsAggregation: AggregationServerState.Minute,
			jobsMode: ChartMode.HOUR,
			jobsHidden: [],
			jobs: [],
			averageAggregation: AggregationServerState.Minute,
			averageMode: ChartMode.HOUR,
			averageHidden: [],
			average: [],
			formatAggregation: AggregationServerState.Minute,
			formatMode: ChartMode.HOUR,
			formatHidden: [],
			formatJobs: [],
			formatAverage: [],
			formatType: Webservice.Converter,
			formatLabelHide: 0,
			errorAggregation: AggregationServerState.Minute,
			errorMode: ChartMode.HOUR,
			errorHidden: [
				Webservice.Converter, Webservice.Barcode, Webservice.Ocr, Webservice.Signature, Webservice.Pdfa,
				Webservice.Urlconverter
			],
			error: []
		};

		this.getStatistics = this.getStatistics.bind(this);
		this.updateTimer = new Timer(async () => {
			if (!FunctionHelper.exists(this.props.context.user)) {
				return;
			}

			let administration: Administration = this.props.context.session.getAdministration() as Administration;

			try {
				await administration.updateServerStatus();
				await this.getStatistics();
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await administration.updateServerStatus();
					await this.getStatistics();
				}
			}
		}, 5000, true);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		let administration: Administration = this.props.context.session.getAdministration() as Administration;

		if (FunctionHelper.exists(this.props.context.user)) {
			try {
				await administration.updateServerStatus();
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await administration.updateServerStatus();
				}
			}
		}

		this.updateTimer.start();
		this.forceUpdate();
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();
		this.updateTimer.pause();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<StatisticsPanelProperties>, prevState: Readonly<StatisticsPanelState>, snapshot?: any
	): Promise<void> {
		if (this.props.contentMode !== prevProps.contentMode) {
			this.updateTimer.pause();

			if (this.props.contentMode === ContentMode.STATISTICS) {
				this.updateTimer.start();
			}
		}
	}

	private getAggregation(): AggregationServerState {
		switch (this.state.panel) {
			case StatisticPanel.TIME:
				return this.state.averageAggregation;
			case StatisticPanel.JOBS:
				return this.state.jobsAggregation;
			case StatisticPanel.FORMAT:
				return this.state.formatAggregation;
			case StatisticPanel.USAGE:
				return this.state.usageAggregation;
			case StatisticPanel.ERROR:
				return this.state.errorAggregation;
		}

		return AggregationServerState.Minute;
	}

	private getMode(): ChartMode {
		switch (this.state.panel) {
			case StatisticPanel.TIME:
				return this.state.averageMode;
			case StatisticPanel.JOBS:
				return this.state.jobsMode;
			case StatisticPanel.FORMAT:
				return this.state.formatMode;
			case StatisticPanel.USAGE:
				return this.state.usageMode;
			case StatisticPanel.ERROR:
				return this.state.errorMode;
		}

		return ChartMode.HOUR;
	}

	private async getStatistics(): Promise<void> {
		if (this.props.contentMode !== ContentMode.STATISTICS || this.state.panel === StatisticPanel.OVERVIEW) {
			return;
		}

		let serverStatus: ServerStatus | undefined = ServerStatus.fromJson(this.props.serverStatus?.get());
		if (!FunctionHelper.exists(serverStatus)) {
			return;
		}

		let administration: Administration = this.props.context.session.getAdministration();
		let endDate: Date = new Date();
		let startDate: Date = new Date();
		startDate.setMinutes(startDate.getMinutes() - this.getMode());

		try {
			let webservices: Array<Webservice> = [];
			for (let [key, webservice] of Object.entries(serverStatus.webservices!)) {
				if (webservice.enabled) {
					webservices.push(key as Webservice);
				}
			}

			if (webservices.length === 0) {
				return;
			}

			let result: AdminStatistic = await administration.getStatistic(
				DataSourceServerState.Realtime, this.getAggregation(), webservices, startDate, endDate
			);

			let statistics: Statistic = new Statistic(result);
			statistics.setStart(startDate);
			statistics.setEnd(endDate);

			switch (this.state.panel) {
				case StatisticPanel.FORMAT:
					this.setState({
						formatAverage: statistics.getFormatAverage(this.state.formatType),
						formatJobs: statistics.getFormatJobs(this.state.formatType)
					});
					break;
				case StatisticPanel.JOBS:
					this.setState({
						jobs: statistics.getJobs(this.getAggregation())
					});
					break;
				case StatisticPanel.TIME:
					this.setState({
						average: statistics.getAverage(this.getAggregation())
					});
					break;
				case StatisticPanel.USAGE:
					this.setState({
						usagePool: statistics.getPoolUsage(this.getAggregation()),
						usageQueue: statistics.getQueueUsage(this.getAggregation())
					});
					break;
				case StatisticPanel.ERROR:
					this.setState({
						error: statistics.getErrors(this.getAggregation())
					});
					break;
			}
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.getStatistics();
			}
			return;
		}
	}

	private computeGradientColor(percentage: number, opacity: number): string {
		let hue: string = ((1 - percentage) * 120).toString(10);
		return ["hsla(", hue, ",100%,50%,", opacity, ")"].join("");
	}

	private renderStatus(status?: string): ReactNode {
		switch (status?.toLowerCase() as WebserviceStatus) {
			case WebserviceStatus.OK:
				return (
					<div className="status">
						<span className="icon icon-play tooltip" data-pr-position={"right"}
							  data-pr-tooltip={Translate.value(
								  "adminscreen.panels.statistics.webservices.status.ok", {ns: "screen"}
							  )}/>
					</div>
				);
			case WebserviceStatus.DISABLED:
				return (
					<div className="status">
						<span className="icon icon-pause tooltip" data-pr-position={"right"}
							  data-pr-tooltip={Translate.value(
								  "adminscreen.panels.statistics.webservices.status.disabled",
								  {ns: "screen"}
							  )}/>
					</div>
				);
			case WebserviceStatus.ERROR:
				return (
					<div className="status">
						<span className="icon icon-warning tooltip" data-pr-position={"right"}
							  data-pr-tooltip={Translate.value(
								  "adminscreen.panels.statistics.webservices.status.error", {ns: "screen"}
							  )}/>
					</div>
				);
			case WebserviceStatus.NOT_LICENSED:
				return (
					<div className="status">
						<span className="icon icon-stop tooltip" data-pr-position={"right"}
							  data-pr-tooltip={Translate.value(
								  "adminscreen.panels.statistics.webservices.status.not_licensed",
								  {ns: "screen"}
							  )}/>
					</div>
				);
		}

		return (
			<div className="status">
				<span className="icon icon-help tooltip" data-pr-position={"right"}
					  data-pr-tooltip={Translate.value(
						  "adminscreen.panels.statistics.webservices.status.unknown", {ns: "screen"}
					  )}/>
			</div>
		);
	}

	private renderOverviewTab(): ReactNode {
		let serverStatus: ServerStatus | undefined = ServerStatus.fromJson(this.props.serverStatus?.get());
		if (!FunctionHelper.exists(serverStatus?.webservices)) {
			return null;
		}

		let values: Array<WebserviceItem> = [];
		for (let [name, webservice] of Object.entries(serverStatus.webservices!)) {
			values.push({
				name: name,
				service: webservice
			});
		}

		return (
			<fieldset className="p-grid">
				<div className="p-col-12">
					<DataTable value={values} responsiveLayout={"scroll"}>
						<Column field="status" header={
							<Translate {...this.props} data="adminscreen.panels.statistics.webservices.status.label"
									   namespace="screen"/>
						} body={(item: WebserviceItem) => {
							return this.renderStatus(item.service.status);
						}}/>
						<Column field="type" header={
							<Translate {...this.props} data="adminscreen.panels.statistics.webservices.type"
									   namespace="screen"/>
						} body={(item: WebserviceItem) => {
							return <Translate {...this.props} namespace="screen"
											  data={"adminscreen.panels.statistics.webservices." + item.name}/>
						}}/>
						<Column field="jobs" header={
							<>
								<Translate {...this.props} namespace="screen"
										   data="adminscreen.panels.statistics.webservices.jobs.label"/>
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={"global.screen.admin.statistic.jobs"} options={{ns: "help"}}/>
							</>
						} body={(item: WebserviceItem) => {
							return (
								<>
									<div className="jobs success tooltip" data-pr-position={"left"}
										 data-pr-tooltip={Translate.value(
											 "adminscreen.panels.statistics.webservices.jobs.successful",
											 {
												 ns: "screen",
												 count: FunctionHelper.applyValue(
													 item.service.state?.jobs?.numberOfSuccessfulJobs,
													 JobServerState.getNumberOfSuccessfulJobsDefault()
												 )
											 }
										 )}>
										{FunctionHelper.applyValue(
											item.service.state?.jobs?.numberOfSuccessfulJobs,
											JobServerState.getNumberOfSuccessfulJobsDefault()
										)}
									</div>
									<div className="jobs error tooltip" data-pr-position={"left"}
										 data-pr-tooltip={Translate.value(
											 "adminscreen.panels.statistics.webservices.jobs.error",
											 {
												 ns: "screen",
												 count: FunctionHelper.applyValue(
													 item.service.state?.jobs?.numberOfErrorJobs,
													 JobServerState.getNumberOfErrorJobsDefault()
												 )
											 }
										 )}>
										{FunctionHelper.applyValue(
											item.service.state?.jobs?.numberOfErrorJobs,
											JobServerState.getNumberOfErrorJobsDefault()
										)}
									</div>
									<div className="jobs tooltip" data-pr-position={"left"}
										 data-pr-tooltip={Translate.value(
											 "adminscreen.panels.statistics.webservices.jobs.count",
											 {
												 ns: "screen",
												 count: FunctionHelper.applyValue(
													 item.service.state?.jobs?.numberOfJobs,
													 JobServerState.getNumberOfJobsDefault()
												 )
											 }
										 )}>
										{FunctionHelper.applyValue(
											item.service.state?.jobs?.numberOfJobs,
											JobServerState.getNumberOfJobsDefault()
										)}
									</div>
								</>
							)
						}}/>
						<Column field="pool" header={
							<>
								<Translate {...this.props} namespace="screen"
										   data="adminscreen.panels.statistics.webservices.pool.label"/>
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={"global.screen.admin.statistic.instances"} options={{ns: "help"}}/>
							</>
						} body={(item: WebserviceItem) => {
							let poolWorkload: number =
								FunctionHelper.applyValue(
									item.service.state?.instances?.poolSize, PoolServerState.getPoolSizeDefault()
								) /
								FunctionHelper.applyValue(
									item.service.state?.instances?.maxPoolSize, PoolServerState.getMaxPoolSizeDefault()
								);

							return (
								<>
									<div className="pool tooltip" data-pr-position={"left"}
										 data-pr-tooltip={Translate.value(
											 "adminscreen.panels.statistics.webservices.pool.used",
											 {
												 ns: "screen",
												 count: FunctionHelper.applyValue(
													 item.service.state?.instances?.poolSize,
													 PoolServerState.getPoolSizeDefault()
												 )
											 }
										 )} style={{background: this.computeGradientColor(poolWorkload, 0.25)}}>
										{FunctionHelper.applyValue(
											item.service.state?.instances?.poolSize,
											PoolServerState.getPoolSizeDefault()
										)}
									</div>
									<div className="pool tooltip" data-pr-position={"left"}
										 data-pr-tooltip={Translate.value(
											 "adminscreen.panels.statistics.webservices.pool.max",
											 {
												 ns: "screen",
												 count: FunctionHelper.applyValue(
													 item.service.state?.instances?.maxPoolSize,
													 PoolServerState.getMaxPoolSizeDefault()
												 )
											 }
										 )}>
										{FunctionHelper.applyValue(
											item.service.state?.instances?.maxPoolSize,
											PoolServerState.getMaxPoolSizeDefault()
										)}
									</div>
								</>
							)
						}}/>
						<Column field="queue" header={
							<>
								<Translate {...this.props}
										   data="adminscreen.panels.statistics.webservices.queue.label"
										   namespace="screen"/>
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={"global.screen.admin.statistic.queue"} options={{ns: "help"}}/>
							</>
						} body={(item: WebserviceItem) => {
							let queueWorkload: number =
								FunctionHelper.applyValue(
									item.service.state?.instances?.queueSize,
									PoolServerState.getQueueSizeDefault()
								) /
								FunctionHelper.applyValue(
									item.service.state?.instances?.maxQueueSize,
									PoolServerState.getMaxQueueSizeDefault()
								);
							let queuePeak: number =
								FunctionHelper.applyValue(
									item.service.state?.instances?.maxUsedQueueSize,
									PoolServerState.getMaxUsedQueueSizeDefault()
								) /
								FunctionHelper.applyValue(
									item.service.state?.instances?.maxQueueSize,
									PoolServerState.getMaxQueueSizeDefault()
								);

							return (
								<div className="queue tooltip" data-pr-position={"left"}
									 data-pr-tooltip={Translate.value(
										 "adminscreen.panels.statistics.webservices.queue.tooltip",
										 {
											 ns: "screen",
											 current: item.service.state?.instances?.queueSize,
											 max: item.service.state?.instances?.maxQueueSize,
											 peak: item.service.state?.instances?.maxUsedQueueSize
										 }
									 )}>
									{item.service.enabled &&
										<ProgressBar unit="" label={
											Translate.value(
												"adminscreen.panels.statistics.webservices.queue.value",
												{
													ns: "screen",
													current: item.service.state?.instances?.queueSize,
													max: item.service.state?.instances?.maxQueueSize,
													peak: item.service.state?.instances?.maxUsedQueueSize
												}
											)
										} bars={[
											{
												value: queueWorkload * 100,
												style: {background: this.computeGradientColor(queueWorkload, 0.25)}
											}
										]} marker={[
											{
												value: queuePeak * 100
											}
										]}/>
									}
								</div>
							);
						}}/>
					</DataTable>
				</div>
			</fieldset>
		)
	}

	private renderOptions(): ReactNode {
		let legends: Array<any> = [];
		let legendOptions: Array<any> = [];
		let legendValue: Array<any> = [];

		switch (this.state.panel) {
			case StatisticPanel.TIME:
				for (let average of this.state.average) {
					if (legends.includes(average.id)) {
						continue;
					}
					legends.push(average.id);
					legendOptions.push({
						label: Translate.value(
							"adminscreen.panels.statistics.chart." + average.id, {ns: "screen"}
						),
						value: average.id
					});
				}

				legendValue = [...legends];
				for (let hidden of this.state.averageHidden) {
					legendValue.splice(legendValue.indexOf(hidden), 1);
				}
				break;
			case StatisticPanel.JOBS:
				for (let job of this.state.jobs) {
					if (legends.includes(job.id)) {
						continue;
					}
					legends.push(job.id);
					legendOptions.push({
						label: Translate.value(
							"adminscreen.panels.statistics.chart." + job.id, {ns: "screen"}
						),
						value: job.id
					});
				}

				legendValue = [...legends];
				for (let hidden of this.state.jobsHidden) {
					legendValue.splice(legendValue.indexOf(hidden), 1);
				}
				break;
			case StatisticPanel.USAGE:
				for (let usage of this.state.usagePool) {
					if (legends.includes(usage.id)) {
						continue;
					}
					legends.push(usage.id);
					legendOptions.push({
						label: Translate.value(
							"adminscreen.panels.statistics.chart." + usage.id, {ns: "screen"}
						),
						value: usage.id
					});
				}

				legendValue = [...legends];
				for (let hidden of this.state.usageHidden) {
					legendValue.splice(legendValue.indexOf(hidden), 1);
				}
				break;
			case StatisticPanel.FORMAT:
				for (let format of this.state.formatJobs) {
					if (legends.includes(format.id)) {
						continue;
					}
					legends.push(format.id);
					legendOptions.push({label: format.id, value: format.id});
				}

				legendValue = [...legends];
				for (let hidden of this.state.formatHidden) {
					legendValue.splice(legendValue.indexOf(hidden), 1);
				}
				break;
			case StatisticPanel.ERROR:
				for (let error of this.state.error) {
					if (legends.includes(error.id)) {
						continue;
					}
					legends.push(error.id);
					legendOptions.push({
						label: Translate.value(
							"adminscreen.panels.statistics.chart." + error.id, {ns: "screen"}
						),
						value: error.id
					});
				}

				legendValue = [...legends];
				for (let hidden of this.state.errorHidden) {
					legendValue.splice(legendValue.indexOf(hidden), 1);
				}
				break;
		}

		return (
			<div className="functions">
				<div className="input-group">
					<label htmlFor="control-statistics-mode">
						<Translate {...this.props} data="adminscreen.panels.statistics.mode.label"
								   namespace="screen"/>
					</label>
					<Dropdown value={this.getMode()} inputId="control-statistics-mode"
							  options={[
								  {
									  label: Translate.value(
										  "adminscreen.panels.statistics.mode.hour", {ns: "screen"}
									  ),
									  value: ChartMode.HOUR
								  },
								  {
									  label: Translate.value(
										  "adminscreen.panels.statistics.mode.six_hours", {ns: "screen"}
									  ),
									  value: ChartMode.SIX_HOURS
								  },
								  {
									  label: Translate.value(
										  "adminscreen.panels.statistics.mode.twelve_hours", {ns: "screen"}
									  ),
									  value: ChartMode.TWELVE_HOURS
								  },
								  {
									  label: Translate.value(
										  "adminscreen.panels.statistics.mode.day", {ns: "screen"}
									  ),
									  value: ChartMode.DAY
								  },
								  {
									  label: Translate.value(
										  "adminscreen.panels.statistics.mode.three_days", {ns: "screen"}
									  ),
									  value: ChartMode.THREE_DAYS
								  },
								  {
									  label: Translate.value(
										  "adminscreen.panels.statistics.mode.week", {ns: "screen"}
									  ),
									  value: ChartMode.WEEK
								  },
								  {
									  label: Translate.value(
										  "adminscreen.panels.statistics.mode.month", {ns: "screen"}
									  ),
									  value: ChartMode.MONTH
								  }
							  ]}
							  onChange={(e) => {
								  let aggregation: AggregationServerState = AggregationServerState.Hour;

								  switch (e.value) {
									  case ChartMode.HOUR:
										  aggregation = AggregationServerState.Minute;
										  break;
									  case ChartMode.WEEK:
									  case ChartMode.MONTH:
										  aggregation = AggregationServerState.Day;
										  break;
								  }

								  let update: any = {};
								  switch (this.state.panel) {
									  case StatisticPanel.JOBS:
										  update.jobsAggregation = aggregation;
										  update.jobsMode = e.value;
										  break;
									  case StatisticPanel.TIME:
										  update.averageAggregation = aggregation;
										  update.averageMode = e.value;
										  break;
									  case StatisticPanel.FORMAT:
										  update.formatAggregation = aggregation;
										  update.formatMode = e.value;
										  break;
									  case StatisticPanel.USAGE:
										  update.usageAggregation = aggregation;
										  update.usageMode = e.value;
										  break;
									  case StatisticPanel.ERROR:
										  update.errorAggregation = aggregation;
										  update.errorMode = e.value;
										  break;
								  }

								  this.setState(update, async () => {
									  await this.getStatistics();
								  });
							  }}
							  appendTo={document.getElementById("root")}/>
				</div>
				{this.state.panel === StatisticPanel.FORMAT &&
					<div className="input-group">
						<label htmlFor="control-statistics-webservice">
							<Translate {...this.props} data="adminscreen.panels.statistics.webservices.type"
									   namespace="screen"/>
						</label>
						<Dropdown value={this.state.formatType} inputId="control-statistics-webservice"
								  options={[
									  {
										  label: Translate.value(
											  "adminscreen.panels.statistics.webservices.converter",
											  {ns: "screen"}
										  ),
										  value: "converter"
									  },
									  {
										  label: Translate.value(
											  "adminscreen.panels.statistics.webservices.ocr",
											  {ns: "screen"}
										  ),
										  value: "ocr"
									  }
								  ]}
								  onChange={(e) => {
									  this.setState({
										  formatType: e.value
									  }, async () => {
										  await this.getStatistics();
									  });
								  }}
								  appendTo={document.getElementById("root")}/>
					</div>
				}
				<div className="input-group">
					<label htmlFor="control-statistics-legend">
						{this.state.panel === StatisticPanel.FORMAT &&
							<Translate {...this.props} data="adminscreen.panels.statistics.chart.legend.label.formats"
									   namespace="screen"/>
						}
						{
							(
								this.state.panel === StatisticPanel.TIME || this.state.panel === StatisticPanel.JOBS ||
								this.state.panel === StatisticPanel.USAGE || this.state.panel === StatisticPanel.ERROR
							) &&
							<Translate {...this.props} namespace="screen"
									   data="adminscreen.panels.statistics.chart.legend.label.webservice"/>
						}
					</label>
					{this.state.panel === StatisticPanel.ERROR &&
						<Dropdown value={legendValue[0]} inputId="control-statistics-legend"
								  options={legendOptions}
								  onChange={(e) => {
									  let hidden: Array<any> = [...legends];
									  hidden.splice(hidden.indexOf(e.value), 1);

									  switch (this.state.panel) {
										  case StatisticPanel.ERROR:
											  this.setState({
												  errorHidden: hidden
											  });
											  break;
									  }
								  }}
								  appendTo={document.getElementById("root")}/>
					}
					{this.state.panel !== StatisticPanel.ERROR &&
						<MultiSelect display="comma" value={legendValue} options={legendOptions} selectAll={true}
									 id="control-statistics-legend"
									 onChange={(e) => {
										 let hidden: Array<any> = [...legends];
										 for (let visible of e.value) {
											 hidden.splice(hidden.indexOf(visible), 1);
										 }

										 switch (this.state.panel) {
											 case StatisticPanel.TIME:
												 this.setState({
													 averageHidden: hidden
												 });
												 break;
											 case StatisticPanel.JOBS:
												 this.setState({
													 jobsHidden: hidden
												 });
												 break;
											 case StatisticPanel.FORMAT:
												 this.setState({
													 formatHidden: hidden
												 });
												 break;
											 case StatisticPanel.USAGE:
												 this.setState({
													 usageHidden: hidden
												 });
												 break;
										 }
									 }}/>
					}
				</div>
				{this.state.panel === StatisticPanel.FORMAT &&
					<div className="input-group">
						<label htmlFor="control-statistics-label-hide">
							<Translate {...this.props} data="adminscreen.panels.statistics.chart.label.hide"
									   namespace="screen"/>
						</label>
						<InputNumber value={this.state.formatLabelHide} min={0} max={100} step={1}
									 inputId="control-statistics-label-hide" showButtons={true} useGrouping={false}
									 onChange={(e) => this.setState({
										 formatLabelHide: FunctionHelper.applyValue(e.value, 0)
									 })} suffix={"%"}/>
					</div>
				}
			</div>
		);
	}

	private renderStatisticsTab(): ReactNode {
		let timeFormat: string = "";
		let tickValues: string = "";

		switch (this.getAggregation()) {
			case AggregationServerState.Minute:
				timeFormat = Translate.value(
					"adminscreen.panels.statistics.chart.legend.date.minute", {ns: "screen"}
				);
				tickValues = "every 5 minutes";

				switch (this.getMode()) {
					case ChartMode.SIX_HOURS:
						tickValues = "every 30 minutes";
						break;
				}
				break;
			case AggregationServerState.Hour:
				timeFormat = Translate.value(
					"adminscreen.panels.statistics.chart.legend.date.hour", {ns: "screen"}
				);
				tickValues = "every 1 hour";

				switch (this.getMode()) {
					case ChartMode.DAY:
						tickValues = "every 2 hour";
						break;
					case ChartMode.THREE_DAYS:
						tickValues = "every 4 hour";
						break;
					case ChartMode.WEEK:
						tickValues = "every 12 hour";
						break;
				}
				break;
			case AggregationServerState.Day:
				timeFormat = Translate.value(
					"adminscreen.panels.statistics.chart.legend.date.day", {ns: "screen"}
				);
				tickValues = "every 1 day";
				break;
		}

		let serverStatus: ServerStatus | undefined = ServerStatus.fromJson(this.props.serverStatus?.get());
		if (!FunctionHelper.exists(serverStatus?.webservices)) {
			return null;
		}

		let maxPoolSize: number = PoolServerState.getMaxPoolSizeDefault();
		let maxQueueSize: number = PoolServerState.getMaxQueueSizeDefault();

		for (let webservice of Object.values(serverStatus.webservices!)) {
			if (FunctionHelper.applyValue(
				webservice.state?.instances?.maxPoolSize, PoolServerState.getMaxPoolSizeDefault()
			) > maxPoolSize) {
				maxPoolSize = FunctionHelper.applyValue(
					webservice.state?.instances?.maxPoolSize, PoolServerState.getMaxPoolSizeDefault()
				);
				maxQueueSize = FunctionHelper.applyValue(
					webservice.state?.instances?.maxQueueSize, PoolServerState.getMaxQueueSizeDefault()
				);
			}
		}

		let gradientBackground: CanvasLayer = (props: CustomCanvasLayerProps) => {
			let gradient: CanvasGradient = props.ctx.createLinearGradient(
				props.innerWidth / 2, 0, props.innerWidth / 2, props.innerHeight
			);
			let opacity: number = 0.25;
			gradient.addColorStop(0, "rgba(255, 0, 0, " + opacity + ")");
			gradient.addColorStop(0.5, "rgba(255, 255, 0, " + opacity + ")");
			gradient.addColorStop(1, "rgba(0, 255, 0, " + opacity + ")");
			props.ctx.fillStyle = gradient;
			props.ctx.fillRect(0, 0, props.innerWidth, props.innerHeight);
		};

		switch (this.state.panel) {
			case StatisticPanel.JOBS:
				let jobs: Array<Serie> = [];
				for (let job of this.state.jobs) {
					if (this.state.jobsHidden.includes(job.id as Webservice)) {
						continue;
					}

					jobs.push(job);
				}

				return (
					<>
						{this.renderOptions()}
						<div className="statistics">
							{this.state.panel === StatisticPanel.JOBS &&
								<LineChart {...this.props} data={jobs} tickValues={tickValues}
										   timeFormat={timeFormat}
										   tooltip={(point: any) => {
											   let date: Date = point?.point?.data?.x;

											   return (
												   <>
													   <div className="p-tooltip">
														   <div className="p-tooltip-text">
															   <p>
																  <span className="color"
																		style={{backgroundColor: point?.point?.serieColor}}/>
																   {point?.point?.serieId}
															   </p>
															   <p>{DateHelper.buildTimestampString(date, this.props.language)}</p>
															   <p>
																   {point?.point?.data?.y + " " + Translate.value(
																	   "adminscreen.panels.statistics.chart.legend.jobs",
																	   {ns: "screen"}
																   ) + " "}
																   (Ø {point?.point?.data?.average + " " + TimeUnit.SECOND})
															   </p>
														   </div>
													   </div>
												   </>
											   );
										   }}
										   legendX={Translate.value(
											   "adminscreen.panels.statistics.chart.legend.date.label",
											   {ns: "screen"}
										   )}
										   legendY={Translate.value(
											   "adminscreen.panels.statistics.chart.legend.jobs",
											   {ns: "screen"}
										   )}/>
							}
						</div>
					</>
				);
			case StatisticPanel.TIME:
				let averages: Array<Serie> = [];
				for (let average of this.state.average) {
					if (this.state.averageHidden.includes(average.id as Webservice)) {
						continue;
					}

					averages.push(average);
				}

				return (
					<>
						{this.renderOptions()}
						<div className="statistics">
							<LineChart {...this.props} data={averages} tickValues={tickValues}
									   timeFormat={timeFormat}
									   tooltip={(point: any) => {
										   let date: Date = point?.point?.data?.x;

										   return (
											   <>
												   <div className="p-tooltip">
													   <div className="p-tooltip-text">
														   <p>
															  <span className="color"
																	style={{backgroundColor: point?.point?.serieColor}}/>
															   {point?.point?.serieId}
														   </p>
														   <p>{DateHelper.buildTimestampString(date, this.props.language)}</p>
														   <p>
															   {point?.point?.data?.y + " " + TimeUnit.SECOND + " "}
															   ({point?.point?.data?.jobs + " " + Translate.value(
															   "adminscreen.panels.statistics.chart.legend.jobs",
															   {ns: "screen"}
														   )})
														   </p>
													   </div>
												   </div>
											   </>
										   );
									   }}
									   legendX={Translate.value(
										   "adminscreen.panels.statistics.chart.legend.date.label",
										   {ns: "screen"}
									   )}
									   legendY={Translate.value(
										   "adminscreen.panels.statistics.chart.legend.average",
										   {ns: "screen"}
									   )}/>
						</div>
					</>
				);
			case StatisticPanel.USAGE:
				let usagePool: Array<Serie> = [];
				for (let pool of this.state.usagePool) {
					if (this.state.usageHidden.includes(pool.id as Webservice)) {
						continue;
					}

					usagePool.push(pool);
				}

				let usageQueue: Array<Serie> = [];
				for (let queue of this.state.usageQueue) {
					if (this.state.usageHidden.includes(queue.id as Webservice)) {
						continue;
					}

					usageQueue.push(queue);
				}

				return (
					<>
						{this.renderOptions()}
						<div className="statistics">
							<div className="p-grid">
								<LineChart {...this.props} data={usagePool} tickValues={tickValues}
										   timeFormat={timeFormat} colors={{scheme: "dark2"}}
										   yScale={{
											   type: "linear",
											   min: 0,
											   max: maxPoolSize
										   }}
										   layers={[
											   gradientBackground, 'grid', 'markers', 'axes', 'areas', 'crosshair',
											   'lines', 'points', 'slices', 'mesh', 'legends'
										   ]}
										   tooltip={(point: any) => {
											   let date: Date = point?.point?.data?.x;

											   return (
												   <>
													   <div className="p-tooltip">
														   <div className="p-tooltip-text">
															   <p>
																  	<span className="color"
																		  style={{backgroundColor: point?.point?.serieColor}}/>
																   {point?.point?.serieId}
															   </p>
															   <p>{DateHelper.buildTimestampString(date, this.props.language)}</p>
															   <p>{point?.point?.data?.y + " " + Translate.value(
																   "adminscreen.panels.statistics.chart.legend.pool",
																   {ns: "screen"}
															   )}</p>
														   </div>
													   </div>
												   </>
											   );
										   }}
										   label={
											   Translate.value(
												   "adminscreen.panels.statistics.chart.legend.pool",
												   {ns: "screen"}
											   )
										   }
										   legendX={Translate.value(
											   "adminscreen.panels.statistics.chart.legend.date.label",
											   {ns: "screen"}
										   )}
										   legendY={Translate.value(
											   "adminscreen.panels.statistics.chart.legend.pool",
											   {ns: "screen"}
										   )}/>
								<LineChart {...this.props} data={usageQueue} tickValues={tickValues}
										   timeFormat={timeFormat} colors={{scheme: "dark2"}}
										   layers={[
											   gradientBackground, 'grid', 'markers', 'axes', 'areas', 'crosshair',
											   'lines', 'points', 'slices', 'mesh', 'legends'
										   ]}
										   yScale={{
											   type: "symlog",
											   min: 0,
											   max: maxQueueSize,
											   constant: 5
										   }}
										   tooltip={(point: any) => {
											   let date: Date = point?.point?.data?.x;

											   return (
												   <>
													   <div className="p-tooltip">
														   <div className="p-tooltip-text">
															   <p>
																   <span className="color"
																		 style={{backgroundColor: point?.point?.serieColor}}/>
																   {point?.point?.serieId}
															   </p>
															   <p>{DateHelper.buildTimestampString(date, this.props.language)}</p>
															   <p>{point?.point?.data?.y + " " + Translate.value(
																   "adminscreen.panels.statistics.chart.legend.queue",
																   {ns: "screen"}
															   )}</p>
														   </div>
													   </div>
												   </>
											   );
										   }}
										   label={
											   Translate.value(
												   "adminscreen.panels.statistics.chart.legend.queue",
												   {ns: "screen"}
											   )
										   }
										   legendX={Translate.value(
											   "adminscreen.panels.statistics.chart.legend.date.label",
											   {ns: "screen"}
										   )}
										   legendY={Translate.value(
											   "adminscreen.panels.statistics.chart.legend.queue",
											   {ns: "screen"}
										   )}/>
							</div>
						</div>
					</>
				);
			case StatisticPanel.FORMAT:
				let formatJobs: Array<any> = [];
				for (let job of this.state.formatJobs) {
					if (this.state.formatHidden.includes(job.id)) {
						continue;
					}

					formatJobs.push(job);
				}

				let formatAverage: Array<any> = [];
				for (let average of this.state.formatAverage) {
					if (this.state.formatHidden.includes(average.id)) {
						continue;
					}

					formatAverage.push(average);
				}

				return (
					<>
						{this.renderOptions()}
						<div className="statistics">
							<div className="p-grid">
								<PieChart {...this.props} data={formatJobs} legends={[]}
										  labelThreshold={this.state.formatLabelHide}
										  layers={['arcLinkLabels', 'arcs', 'arcLabels', 'legends',
											  ({dataWithArc, centerX, centerY}) => {
												  let total = 0
												  dataWithArc.forEach(datum => {
													  total += datum.value
												  })

												  return (
													  <foreignObject x={centerX} y={centerY} width="200" height="200"
																	 style={{
																		 overflow: "visible"
																	 }}>
														  <p style={{
															  fontSize: '30px',
															  fontWeight: 600,
															  textAlign: "center",
															  transform: "translate(-50%, -50%)"
														  }}>
															  {total} <Translate {...this.props} namespace="screen"
																				 data="adminscreen.panels.statistics.chart.legend.jobs"/>
														  </p>
													  </foreignObject>
												  )
											  }
										  ]}
										  label={Translate.value(
											  "adminscreen.panels.statistics.tabs.jobs", {ns: "screen"}
										  )}
										  tooltip={(e) => {
											  return <>
												  <div className="p-tooltip">
													  <div className="p-tooltip-text">
														  <p>
															  <span className="color"
																	style={{backgroundColor: e.datum.color}}/>
															  {
																  e.datum.id + ": " +
																  e.datum.value + " " + Translate.value(
																	  "adminscreen.panels.statistics.chart.legend.jobs",
																	  {ns: "screen"}
																  ) + " "
															  }
														  </p>
													  </div>
												  </div>
											  </>;
										  }}
										  arcLabel={(e: any) => {
											  let jobs: number = 0;
											  for (let format of formatJobs) {
												  jobs += format.value;
											  }

											  return NumberHelper.format((e.value / jobs) * 100, 2) + "%";
										  }}/>
								<PieChart {...this.props} data={formatAverage} legends={[]}
										  labelThreshold={this.state.formatLabelHide}
										  layers={['arcLinkLabels', 'arcs', 'arcLabels', 'legends',
											  ({dataWithArc, centerX, centerY}) => {
												  let total = 0
												  dataWithArc.forEach(datum => {
													  total += datum.data.jobs
												  })

												  return (
													  <foreignObject x={centerX} y={centerY} width="200" height="200"
																	 style={{
																		 overflow: "visible"
																	 }}>
														  <p style={{
															  fontSize: '30px',
															  fontWeight: 600,
															  textAlign: "center",
															  transform: "translate(-50%, -50%)"
														  }}>
															  {total} <Translate {...this.props} namespace="screen"
																				 data="adminscreen.panels.statistics.chart.legend.jobs"/>
														  </p>
													  </foreignObject>
												  )
											  }
										  ]}
										  label={Translate.value(
											  "adminscreen.panels.statistics.tabs.time", {ns: "screen"}
										  )}
										  tooltip={(e) => {
											  return <>
												  <div className="p-tooltip">
													  <div className="p-tooltip-text">
														  <p>
															  <span className="color"
																	style={{backgroundColor: e.datum.color}}/>
															  {
																  e.datum.id + ": " +
																  e.datum.value + " " + TimeUnit.SECOND + " "
															  }
														  </p>
													  </div>
												  </div>
											  </>;
										  }}
										  arcLabel={function (e: any) {
											  let average: number = 0;
											  for (let format of formatAverage) {
												  average += format.value;
											  }

											  return NumberHelper.format((e.value / average) * 100, 2) + "%";
										  }}/>
							</div>
						</div>
					</>
				);
			case StatisticPanel.ERROR:
				let errorCount: number = 0;
				let errors: Array<any> = [];
				for (let error of this.state.error) {
					if (this.state.errorHidden.includes(error.id)) {
						continue;
					}

					errors.push(error);
					for (let data of error.data) {
						errorCount += data.y;
					}
				}

				return (
					<>
						{this.renderOptions()}
						<div className="statistics">
							<LineChart {...this.props} data={errors} tickValues={tickValues} legends={[]}
									   timeFormat={timeFormat} curve="stepAfter" enablePoints={false} enableArea={true}
									   yScale={errorCount === 0 ? {
										   type: "linear",
										   min: 0,
										   max: 1
									   } : undefined}
									   tooltip={(point: any) => {
										   let date: Date = point?.point?.data?.x;

										   return (
											   <>
												   <div className="p-tooltip">
													   <div className="p-tooltip-text">
														   <p>
														  <span className="color"
																style={{backgroundColor: point?.point?.serieColor}}/>
															   {point?.point?.serieId}
														   </p>
														   <p>{DateHelper.buildTimestampString(date, this.props.language)}</p>
														   <p>{point?.point?.data?.y + " " + Translate.value(
															   "adminscreen.panels.statistics.chart.legend.error",
															   {ns: "screen"}
														   )}</p>
													   </div>
												   </div>
											   </>
										   );
									   }}
									   legendX={Translate.value(
										   "adminscreen.panels.statistics.chart.legend.date.label",
										   {ns: "screen"}
									   )}
									   legendY={Translate.value(
										   "adminscreen.panels.statistics.chart.legend.error",
										   {ns: "screen"}
									   )}/>
						</div>
					</>
				);
		}
	}

	protected renderError(error?: Exception): ReactNode {
		let exception: Exception | undefined = FunctionHelper.applyValue(error, this.state.exception);

		if (!FunctionHelper.exists(exception)) {
			return null;
		}

		return (
			<fieldset className="p-col-12 p-grid">
				{super.renderError(error)}
			</fieldset>
		)
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let serverStatus: ServerStatus | undefined = ServerStatus.fromJson(this.props.serverStatus?.get());
		if (!FunctionHelper.exists(serverStatus?.webservices)) {
			return null;
		}

		let formatWebservices: Array<Webservice> = [];
		let webservices: Array<Webservice> = [];
		for (let [key, webservice] of Object.entries(serverStatus.webservices!)) {
			if (!webservice.enabled) {
				continue;
			}

			webservices.push(key as Webservice);

			if ([Webservice.Ocr, Webservice.Converter].includes(key as Webservice)) {
				formatWebservices.push(key as Webservice);
			}
		}

		return (
			<div className="screen admin-statistics-panel" data-help="global.screen.admin.content" data-position="top">
				<div className="body">
					<h1>
						<Translate {...this.props} data="adminscreen.panels.statistics.title" namespace="screen"/>
						{this.updateTimer.isRunning() ?
							<MenuButton {...this.props} icon="icon icon-pause"
										tooltip={Translate.value(
											"adminscreen.panels.statistics.stop", {ns: "screen"}
										)}
										onClick={() => {
											this.updateTimer.pause();
											this.forceUpdate();
										}}/> :
							<MenuButton {...this.props} icon="icon icon-play"
										tooltip={Translate.value(
											"adminscreen.panels.statistics.start", {ns: "screen"}
										)}
										onClick={() => {
											this.updateTimer.start();
											this.forceUpdate();
										}}/>
						}
						{this.renderHelpButton()}
					</h1>
					<Helper {...this.props}>
						<Translate {...this.props} data={"global.screen.admin.statistic.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid">
						{this.renderError()}
					</fieldset>
					<TabView activeIndex={this.state.panel}
							 onTabChange={(e) => {
								 this.setState({
									 panel: e.index
								 }, async () => {
									 if (this.state.panel !== StatisticPanel.OVERVIEW) {
										 await this.getStatistics();
									 }
								 });
							 }}>
						<TabPanel header={
							Translate.value("adminscreen.panels.statistics.tabs.overview", {ns: "screen"})
						}>
							{this.renderOverviewTab()}
						</TabPanel>
						{webservices.length > 0 &&
							<TabPanel header={Translate.value(
								"adminscreen.panels.statistics.tabs.jobs", {ns: "screen"}
							)}>
								{this.renderStatisticsTab()}
							</TabPanel>
						}
						{webservices.length > 0 &&
							<TabPanel header={Translate.value(
								"adminscreen.panels.statistics.tabs.usage", {ns: "screen"}
							)}>
								{this.renderStatisticsTab()}
							</TabPanel>
						}
						{webservices.length > 0 &&
							<TabPanel header={Translate.value(
								"adminscreen.panels.statistics.tabs.error", {ns: "screen"}
							)}>
								{this.renderStatisticsTab()}
							</TabPanel>
						}
						{webservices.length > 0 &&
							<TabPanel header={Translate.value(
								"adminscreen.panels.statistics.tabs.time", {ns: "screen"}
							)}>
								{this.renderStatisticsTab()}
							</TabPanel>
						}
						{formatWebservices.length > 0 &&
							<TabPanel header={Translate.value(
								"adminscreen.panels.statistics.tabs.format", {ns: "screen"}
							)}>
								{this.renderStatisticsTab()}
							</TabPanel>
						}
					</TabView>
				</div>
			</div>
		);
	}
}

const StatisticsPanel = React.memo(StatisticsPanelDefinition,
	(prevProps: Readonly<StatisticsPanelProperties>, nextProps: Readonly<StatisticsPanelProperties>) => {
		if (nextProps.contentMode !== prevProps.contentMode) {
			return false;
		}

		if (prevProps.language !== nextProps.language) {
			return false;
		}

		if (!prevProps.serverStatus?.equals(nextProps.serverStatus)) {
			return false;
		}

		if (prevProps.showHelp !== nextProps.showHelp) {
			return false;
		}

		return true;
	}
);

export default StatisticsPanel;